// AiHomeSession.jsx — AI chat session surface embedded on Home hub

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { ShellSectionActions, usePublishShellChrome } from 'context/ShellChromeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import { useIsMobile, useMaxWidth } from 'hooks/useDeviceDetect';

import ChannelPostForm from 'channels/components/ChannelPostForm';
import ChannelThreadPanel from 'channels/components/ChannelThreadPanel';
import ChannelToolbar from 'channels/components/ChannelToolbar';
import MessageFeed from 'channels/components/MessageFeed';
import { CHANNEL_TYPES, isAiConversation } from 'channels/constants/channelTypes';
import useChannelAttachmentNodes from 'channels/hooks/useChannelAttachmentNodes';
import useChannelMessages from 'channels/hooks/useChannelMessages';
import useChannelThreadMessages from 'channels/hooks/useChannelThreadMessages';
import useIncomingMessageSound from 'channels/hooks/useIncomingMessageSound';
import useBaseChannels from 'channels/hooks/useBaseChannels';
import { buildMentionCandidates } from 'channels/utils/channelMentions';
import { resolveThreadSummary } from 'channels/utils/threadSummaries';
import { isThreadUnread } from 'channels/utils/threadUnread';
import { getChannelMessage, subscribeToChannelReadReceipt } from 'channels/services/ChannelReadService';
import {
  clearChannelMessages,
  markThreadRead,
  postChannelMessage,
} from 'channels/services/ChannelWriteService';
import Confirm from 'components/confirm/Confirm';
import Button from 'components/button/Button';
import PeekPanel from 'node/peek/PeekPanel';
import usePeekPanelController from 'node/hooks/usePeekPanelController';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';
import {
  MESSAGE_TYPES,
  AI_CONVERSATION_KIND,
  AI_LEGACY_FEED_TITLES,
  AI_NEW_CHAT_TITLE,
  AI_SESSION_KINDS,
  AI_TITLE_SOURCES,
  AI_UPDATES_TITLE,
} from 'spaces/constants/aiConstants';
import AiSessionsPanel from 'spaces/components/AiSessionsPanel';
import useAiFeedReadSync from 'spaces/hooks/useAiFeedReadSync';
import useBasesAnalyze from 'spaces/hooks/useBasesAnalyze';
import {
  ensureAiChannel,
  updateAiSessionMeta,
} from 'spaces/services/aiConversationService';
import { resolveAiNavigateTo } from 'spaces/utils/buildChannelDeepLinkPath';
import { buildAiHomePath } from 'spaces/utils/buildAiHomePath';
import {
  buildAiSessionAutoTitle,
  shouldApplyAiAutoTitle,
} from 'spaces/utils/buildAiSessionAutoTitle';
import {
  buildAiFeedChannelKey,
  isAiChannelKeyForUser,
  isAiFeedChannelKeyForUser,
  isAiFeedSession,
} from 'spaces/utils/aiChannelKeys';
import {
  getSessionsPanelCollapsed,
  setLastActiveSessionKey,
  setSessionsPanelCollapsed,
} from 'spaces/utils/aiSessionPrefs';
import { buildAiSessionsList } from 'spaces/utils/sortAiSessions';
import { generateKey } from 'utilities/Keys';
import { getPersonName } from 'utilities/PersonIdentity';
import { buildUserSummary } from 'utilities/Users';
import { buildBaseMemberProfile } from 'utilities/BaseMembers';
import { subscribeToBaseMembers } from 'services/bases/BaseMemberService';
import { subscribeToMentionedPeople } from 'services/mentionedPeople/MentionedPeopleService';

import 'channels/Channel.css';
import './AiHomeSession.css';

function cleanString(value) {
  return String(value || '').trim();
}

const AiHomeSession = React.memo(({
  sessionKey = '',
  onBackToIdle,
}) => {
  const { user } = useUser();
  const { activeBase } = useBase();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNarrow = useMaxWidth(1120);
  const isMobile = useIsMobile();

  const baseKey = cleanString(activeBase?.key);
  const userKey = cleanString(user?.key);
  const channelKey = cleanString(sessionKey);
  const activeThreadRootKey = cleanString(searchParams.get('threadRootKey'));
  const scrollToMessageKey = cleanString(searchParams.get('messageKey'));
  const isThreadPanelOpen = Boolean(activeThreadRootKey);

  const processedCaptureHandoffRef = useRef(null);
  const [sessionsPanelCollapsed, setSessionsPanelCollapsedState] = useState(false);
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [baseMembers, setBaseMembers] = useState([]);
  const [mentionedPeople, setMentionedPeople] = useState([]);
  const [channelReadReceipt, setChannelReadReceipt] = useState(null);
  const [fetchedThreadRootMessage, setFetchedThreadRootMessage] = useState(null);

  const { openNode } = useNodeOpenIntent();
  const {
    isPeekOpen,
    closePeek,
    peekLocalNode,
    isPeekLoading,
    peekError,
    handlePeekTitleSaved,
    handlePeekTagsSaved,
    handlePeekLocationSaved,
    handlePeekSubtitleSaved,
    handlePeekCoverSaved,
    handlePeekNodeChange,
  } = usePeekPanelController({
    user,
    setCurrentNode: null,
  });

  const {
    channels,
    markRead,
  } = useBaseChannels({ baseKey, user });

  const feedKey = useMemo(
    () => buildAiFeedChannelKey(baseKey, userKey),
    [userKey, baseKey],
  );

  const routeMatchedChannel = useMemo(
    () => (Array.isArray(channels) ? channels : []).find((row) => row?.key === channelKey) || null,
    [channels, channelKey],
  );

  const selectedChannel = useMemo(() => {
    if (routeMatchedChannel) return routeMatchedChannel;
    if (!channelKey || !isAiChannelKeyForUser(channelKey, baseKey, userKey)) {
      return null;
    }
    const isFeed = channelKey === feedKey;
    return {
      key: channelKey,
      baseKey,
      channelType: CHANNEL_TYPES.CONVERSATION,
      conversationKind: AI_CONVERSATION_KIND,
      sessionKind: isFeed ? AI_SESSION_KINDS.FEED : AI_SESSION_KINDS.CHAT,
      memberKeys: [userKey],
      title: isFeed ? AI_UPDATES_TITLE : AI_NEW_CHAT_TITLE,
      messagingEnabled: true,
    };
  }, [channelKey, feedKey, routeMatchedChannel, userKey, baseKey]);

  const displayTitle = useMemo(() => {
    const stored = cleanString(selectedChannel?.title);
    if (isAiFeedSession(selectedChannel, baseKey, userKey)) {
      if (!stored || AI_LEGACY_FEED_TITLES.includes(stored.toLowerCase())) {
        return AI_UPDATES_TITLE;
      }
      return stored;
    }
    return stored || AI_NEW_CHAT_TITLE;
  }, [selectedChannel, userKey, baseKey]);

  const hideChannelIdentity = Boolean(selectedChannel) && !isMobile;
  const channelActionsBelow = hideChannelIdentity;

  usePublishShellChrome({
    title: displayTitle,
    iconKey: 'network_node',
    enabled: hideChannelIdentity && Boolean(displayTitle),
  });

  const {
    analyzeQuestion,
    analyzeError,
    countPendingAnalyzes,
    hasPendingAnalyze,
  } = useBasesAnalyze({
    baseKey,
    channelKey: selectedChannel ? channelKey : null,
  });

  const activeMessageChannelKey = routeMatchedChannel?.key || channelKey || null;

  const {
    messages,
    threadSummariesByRootKey,
    loading: messagesLoading,
    errorMessage: messagesError,
    isSending,
    hasMoreMessages,
    isLoadingOlder,
    loadOlderMessages,
    removeMessage,
    updateMessage,
  } = useChannelMessages({
    baseKey,
    channelKey: activeMessageChannelKey,
    user,
  });

  const {
    replies: threadReplies,
    loading: threadMessagesLoading,
    errorMessage: threadMessagesError,
    isSending: isThreadSending,
    hasMoreReplies,
    loadMoreReplies,
    sendReply,
    updateMessage: updateThreadMessage,
    removeMessage: removeThreadMessage,
  } = useChannelThreadMessages({
    baseKey,
    channelKey: activeMessageChannelKey,
    threadRootKey: activeThreadRootKey,
    user,
    enabled: isThreadPanelOpen && Boolean(activeMessageChannelKey),
  });

  useIncomingMessageSound({
    messages,
    currentUserKey: userKey,
    enabled: Boolean(activeMessageChannelKey),
    feedKey: activeMessageChannelKey ? `${activeMessageChannelKey}:main` : '',
    loading: messagesLoading,
  });

  useIncomingMessageSound({
    messages: threadReplies,
    currentUserKey: userKey,
    enabled: Boolean(isThreadPanelOpen && activeMessageChannelKey && activeThreadRootKey),
    feedKey: (activeMessageChannelKey && activeThreadRootKey)
      ? `${activeMessageChannelKey}:thread:${activeThreadRootKey}`
      : '',
    loading: threadMessagesLoading,
  });

  const { attachmentNodesByKey } = useChannelAttachmentNodes({
    baseKey,
    messages,
  });

  useAiFeedReadSync({
    enabled: Boolean(routeMatchedChannel && isAiConversation(routeMatchedChannel)),
    messages,
  });

  const aiSessions = useMemo(() => (
    buildAiSessionsList({
      channels,
      baseKey,
      userKey,
    })
  ), [channels, userKey, baseKey]);

  const showSessionsPanel = !sessionsPanelCollapsed
    && !isNarrow
    && !isPeekOpen
    && !isThreadPanelOpen;
  const showSessionsDrawer = sessionsDrawerOpen && isNarrow && !isPeekOpen && !isThreadPanelOpen;
  const pendingCount = countPendingAnalyzes({ threadRootKey: '' });
  const threadPendingAnalyzeCount = countPendingAnalyzes({ threadRootKey: activeThreadRootKey });
  const isFeedSession = isAiFeedSession(selectedChannel, baseKey, userKey);
  const canClearConversation = isFeedSession && (messages || []).length > 0;

  const activeThreadRootMessage = useMemo(() => {
    if (!activeThreadRootKey) return null;
    const fromFeed = (messages || []).find((row) => row?.key === activeThreadRootKey);
    if (fromFeed) return fromFeed;
    if (fetchedThreadRootMessage?.key === activeThreadRootKey) {
      return fetchedThreadRootMessage;
    }
    return null;
  }, [activeThreadRootKey, fetchedThreadRootMessage, messages]);

  const threadAttachmentMessages = useMemo(() => {
    const rows = [];
    if (activeThreadRootMessage) rows.push(activeThreadRootMessage);
    if (Array.isArray(threadReplies)) rows.push(...threadReplies);
    return rows;
  }, [activeThreadRootMessage, threadReplies]);

  const { attachmentNodesByKey: threadAttachmentNodesByKey } = useChannelAttachmentNodes({
    baseKey,
    messages: threadAttachmentMessages,
  });

  const mainFeedScrollToMessageKey = useMemo(() => {
    if (!scrollToMessageKey || isThreadPanelOpen) return '';
    return scrollToMessageKey;
  }, [isThreadPanelOpen, scrollToMessageKey]);

  const threadFeedScrollToMessageKey = useMemo(() => {
    if (!scrollToMessageKey || !isThreadPanelOpen) return '';
    return scrollToMessageKey;
  }, [isThreadPanelOpen, scrollToMessageKey]);

  const hasUnreadRepliesByKey = useMemo(() => (
    (messages || []).reduce((acc, message) => {
      if (!message?.key) return acc;
      const threadSummary = resolveThreadSummary(message, threadSummariesByRootKey);
      acc[message.key] = isThreadUnread({
        rootMessage: {
          ...message,
          replyCount: threadSummary.replyCount,
          lastReplyAt: threadSummary.lastReplyAt || message?.lastReplyAt,
        },
        readReceipt: channelReadReceipt,
        currentUserKey: userKey,
      });
      return acc;
    }, {})
  ), [channelReadReceipt, messages, threadSummariesByRootKey, userKey]);

  const handleOpenAttachmentNode = useCallback((node) => {
    if (!node?.key) return;
    openNode(node, { preferPeek: true });
  }, [openNode]);

  const handleOpenThread = useCallback((threadRootKey) => {
    const cleanRootKey = cleanString(threadRootKey);
    if (!cleanRootKey) return;
    const nextParams = createSearchParams(searchParams);
    nextParams.set('threadRootKey', cleanRootKey);
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const handleCloseThread = useCallback(() => {
    const nextParams = createSearchParams(searchParams);
    nextParams.delete('threadRootKey');

    if (scrollToMessageKey) {
      const isReplyTarget = (threadReplies || []).some((row) => row?.key === scrollToMessageKey);
      if (isReplyTarget) {
        nextParams.delete('messageKey');
      }
    }

    setSearchParams(nextParams);
  }, [scrollToMessageKey, searchParams, setSearchParams, threadReplies]);

  useEffect(() => {
    setSessionsPanelCollapsedState(
      getSessionsPanelCollapsed({ baseKey, userKey }),
    );
    setSessionsDrawerOpen(false);
  }, [userKey, baseKey]);

  useEffect(() => {
    setFetchedThreadRootMessage(null);
  }, [channelKey]);

  useEffect(() => {
    if (!baseKey || !activeMessageChannelKey || !userKey) {
      setChannelReadReceipt(null);
      return undefined;
    }

    const unsubscribe = subscribeToChannelReadReceipt(
      baseKey,
      activeMessageChannelKey,
      userKey,
      (row) => setChannelReadReceipt(row || null),
    );

    return () => unsubscribe?.();
  }, [activeMessageChannelKey, userKey, baseKey]);

  useEffect(() => {
    if (!baseKey || !activeMessageChannelKey || !activeThreadRootKey) return undefined;
    if (activeThreadRootMessage) return undefined;

    let active = true;
    getChannelMessage(baseKey, activeMessageChannelKey, activeThreadRootKey)
      .then((row) => {
        if (!active) return;
        setFetchedThreadRootMessage(row);
      })
      .catch((error) => {
        console.error('[Home AI] fetch thread root error:', error);
      });

    return () => {
      active = false;
    };
  }, [
    activeMessageChannelKey,
    activeThreadRootKey,
    activeThreadRootMessage,
    baseKey,
  ]);

  useEffect(() => {
    if (!baseKey || !activeMessageChannelKey || !scrollToMessageKey || activeThreadRootKey) {
      return undefined;
    }

    let active = true;
    getChannelMessage(baseKey, activeMessageChannelKey, scrollToMessageKey)
      .then((row) => {
        if (!active || !row?.threadRootKey) return;
        const nextParams = createSearchParams(searchParams);
        nextParams.set('threadRootKey', row.threadRootKey);
        setSearchParams(nextParams, { replace: true });
      })
      .catch((error) => {
        console.error('[Home AI] resolve thread deep link error:', error);
      });

    return () => {
      active = false;
    };
  }, [
    activeMessageChannelKey,
    activeThreadRootKey,
    scrollToMessageKey,
    searchParams,
    setSearchParams,
    baseKey,
  ]);

  useEffect(() => {
    if (!baseKey || !activeMessageChannelKey || !userKey || !activeThreadRootKey) return;

    markThreadRead({
      baseKey,
      channelKey: activeMessageChannelKey,
      userKey,
      threadRootKey: activeThreadRootKey,
      lastReadAt: activeThreadRootMessage?.lastReplyAt || undefined,
    }).catch((error) => {
      console.error('[Home AI] mark thread read error:', error);
    });
  }, [
    activeMessageChannelKey,
    activeThreadRootKey,
    activeThreadRootMessage?.lastReplyAt,
    userKey,
    baseKey,
  ]);

  useEffect(() => {
    if (!baseKey || !userKey || !channelKey) return;
    if (!isAiChannelKeyForUser(channelKey, baseKey, userKey)) return;
    setLastActiveSessionKey({ baseKey, userKey, sessionKey: channelKey });
  }, [channelKey, userKey, baseKey]);

  useEffect(() => {
    if (!baseKey || !userKey) return undefined;
    if (routeMatchedChannel) return undefined;
    if (!isAiFeedChannelKeyForUser(channelKey, baseKey, userKey)) {
      return undefined;
    }
    let cancelled = false;
    ensureAiChannel({
      baseKey,
      userKey,
      userName: getPersonName(user, 'Member'),
    }).catch((error) => {
      if (!cancelled) console.error('[Home AI] ensure feed failed', error);
    });
    return () => { cancelled = true; };
  }, [channelKey, routeMatchedChannel, user, userKey, baseKey]);

  useEffect(() => {
    if (!routeMatchedChannel?.key) return;
    markRead({
      channelKey: routeMatchedChannel.key,
      lastReadAt: routeMatchedChannel?.lastMessageAt,
    });
  }, [markRead, routeMatchedChannel?.key, routeMatchedChannel?.lastMessageAt]);

  useEffect(() => {
    if (!baseKey) return undefined;
    const unsubMembers = subscribeToBaseMembers(baseKey, (rows) => {
      setBaseMembers(Array.isArray(rows) ? rows : []);
    });
    const unsubMentions = subscribeToMentionedPeople(baseKey, (rows) => {
      setMentionedPeople(Array.isArray(rows) ? rows : []);
    });
    return () => {
      unsubMembers?.();
      unsubMentions?.();
    };
  }, [baseKey]);

  useEffect(() => {
    if (!channelKey || !routeMatchedChannel) return;
    const handoffState = location.state;
    if (!handoffState?.capturePendingAnalyze) return;

    const userMessageKey = cleanString(handoffState.userMessageKey);
    const question = cleanString(handoffState.question);
    if (!userMessageKey || !question) return;
    if (processedCaptureHandoffRef.current === userMessageKey) return;
    processedCaptureHandoffRef.current = userMessageKey;

    void analyzeQuestion({
      userMessageKey,
      question,
      captureOrigin: handoffState?.captureOrigin === true,
      selectedNodeKey: cleanString(handoffState?.selectedNodeKey),
    }).then((result) => {
      const destination = resolveAiNavigateTo(result);
      if (destination?.path) {
        navigate(destination.path);
        return;
      }
      navigate(buildAiHomePath({ channelKey }), {
        replace: true,
        state: {},
      });
    });
  }, [analyzeQuestion, channelKey, location.state, navigate, routeMatchedChannel]);

  const membersByKey = useMemo(() => {
    const map = {};
    (Array.isArray(baseMembers) ? baseMembers : []).forEach((member) => {
      const profile = buildBaseMemberProfile(member);
      const key = cleanString(profile?.userKey || profile?.key);
      if (key) map[key] = profile;
    });
    return map;
  }, [baseMembers]);

  const mentionedPeopleByKey = useMemo(() => {
    const map = {};
    (Array.isArray(mentionedPeople) ? mentionedPeople : []).forEach((person) => {
      const key = cleanString(person?.key);
      if (!key) return;
      map[key] = person;
      map[`mp:${key}`] = person;
    });
    return map;
  }, [mentionedPeople]);

  const mentionCandidates = useMemo(() => (
    buildMentionCandidates({
      channelMembers: [],
      baseMembers,
      mentionedPeople,
      isConversation: true,
      includeInviteCandidates: false,
      includeAllBaseMembers: true,
    })
  ), [mentionedPeople, baseMembers]);

  const handleToggleSessions = useCallback(() => {
    if (isNarrow) {
      setSessionsDrawerOpen((prev) => !prev);
      return;
    }
    setSessionsPanelCollapsedState((prev) => {
      const next = !prev;
      setSessionsPanelCollapsed({ baseKey, userKey, collapsed: next });
      return next;
    });
  }, [isNarrow, userKey, baseKey]);

  const handleCollapseSessions = useCallback(() => {
    if (isNarrow) {
      setSessionsDrawerOpen(false);
      return;
    }
    setSessionsPanelCollapsedState(true);
    setSessionsPanelCollapsed({ baseKey, userKey, collapsed: true });
  }, [isNarrow, userKey, baseKey]);

  const handleOpenSession = useCallback((nextKey) => {
    const key = cleanString(nextKey);
    if (!key) return;
    setSessionsDrawerOpen(false);
    setLastActiveSessionKey({ baseKey, userKey, sessionKey: key });
    navigate(buildAiHomePath({ channelKey: key }));
  }, [navigate, userKey, baseKey]);

  const handleSessionDeleted = useCallback((deletedKey) => {
    if (deletedKey !== channelKey) return;
    onBackToIdle?.();
  }, [channelKey, onBackToIdle]);

  const handlePostSubmit = useCallback(async (payload = {}) => {
    const text = cleanString(payload?.text);
    const mentionKeys = Array.isArray(payload?.mentionKeys) ? payload.mentionKeys : [];
    const nodeReferences = Array.isArray(payload?.nodeReferences) ? payload.nodeReferences : [];
    const attachments = (() => {
      const seen = new Set();
      return nodeReferences
        .map((row) => {
          const nodeKey = cleanString(row?.nodeKey || row?.key);
          if (!nodeKey || seen.has(nodeKey)) return null;
          seen.add(nodeKey);
          return {
            key: generateKey(),
            type: 'node_ref',
            nodeKey,
            title: cleanString(row?.title),
            nodeType: cleanString(row?.nodeType || row?.type) || 'NODE',
          };
        })
        .filter(Boolean);
    })();

    if ((!text && !attachments.length) || !baseKey || !userKey || !channelKey) {
      return false;
    }

    let targetKey = channelKey;
    if (isAiFeedChannelKeyForUser(targetKey, baseKey, userKey)) {
      const ensured = await ensureAiChannel({
        baseKey,
        userKey,
        userName: getPersonName(user, 'Member'),
        force: true,
      });
      if (!ensured?.channel?.key) return false;
      targetKey = ensured.channel.key;
    }

    const userSummary = buildUserSummary(user);
    const created = await postChannelMessage({
      baseKey,
      channelKey: targetKey,
      userKey,
      user: userSummary,
      userName: userSummary?.name || getPersonName(user, 'Member'),
      userPhoto: userSummary?.coverUrl || '',
      text,
      attachments,
      mentionKeys,
      messageType: MESSAGE_TYPES.USER_TEXT,
    });
    if (!created?.key) return false;

    if (shouldApplyAiAutoTitle(selectedChannel)) {
      try {
        await updateAiSessionMeta({
          baseKey,
          channelKey: targetKey,
          title: buildAiSessionAutoTitle({ question: text }),
          titleSource: AI_TITLE_SOURCES.AUTO,
          userKey,
        });
      } catch (error) {
        console.warn('[Home AI] auto-title failed', error);
      }
    }

    setLastActiveSessionKey({ baseKey, userKey, sessionKey: targetKey });

    const analyzeResult = await analyzeQuestion({
      userMessageKey: created.key,
      question: text,
      mentionKeys,
    });
    const destination = resolveAiNavigateTo(analyzeResult);
    if (destination?.path) navigate(destination.path);
    return true;
  }, [
    analyzeQuestion,
    channelKey,
    navigate,
    selectedChannel,
    user,
    userKey,
    baseKey,
  ]);

  const handleThreadPostSubmit = useCallback(async (payload = {}) => {
    const replyResult = await sendReply(payload);
    const ok = Boolean(replyResult?.ok);
    if (!ok) return ok;

    const text = cleanString(payload?.text);
    const mentionKeys = Array.isArray(payload?.mentionKeys) ? payload.mentionKeys : [];
    if (!text || !activeThreadRootKey || !replyResult?.messageKey) return ok;

    await analyzeQuestion({
      userMessageKey: replyResult.messageKey,
      question: text,
      threadRootKey: activeThreadRootKey,
      mentionKeys,
    });

    return ok;
  }, [
    activeThreadRootKey,
    analyzeQuestion,
    sendReply,
  ]);

  const handleSelectDisambiguationOption = useCallback(async (option = {}) => {
    if (!baseKey || !userKey) return false;

    const nodeKey = cleanString(option?.nodeKey);
    const text = cleanString(option?.title || option?.label);
    if (!nodeKey || !text) return false;

    let targetKey = cleanString(channelKey);
    if (!targetKey) return false;

    if (isAiFeedChannelKeyForUser(targetKey, baseKey, userKey)) {
      const ensured = await ensureAiChannel({
        baseKey,
        userKey,
        userName: getPersonName(user, 'Member'),
        force: true,
      });
      if (!ensured?.channel?.key) {
        console.error('[Home AI] Ai feed session is not provisioned.');
        return false;
      }
      targetKey = ensured.channel.key;
    }

    const userSummary = buildUserSummary(user);
    const created = await postChannelMessage({
      baseKey,
      channelKey: targetKey,
      userKey,
      user: userSummary,
      userName: userSummary?.name || getPersonName(user, 'Member'),
      userPhoto: userSummary?.coverUrl || '',
      text,
      attachments: [],
      mentionKeys: [],
      messageType: MESSAGE_TYPES.USER_TEXT,
    });

    if (!created?.key) return false;

    setLastActiveSessionKey({ baseKey, userKey, sessionKey: targetKey });

    await analyzeQuestion({
      userMessageKey: created.key,
      question: text,
      selectedNodeKey: nodeKey,
      channelKey: targetKey,
    });

    if (targetKey !== channelKey) {
      navigate(buildAiHomePath({ channelKey: targetKey }));
    }

    return true;
  }, [
    analyzeQuestion,
    channelKey,
    navigate,
    user,
    userKey,
    baseKey,
  ]);

  const handleClear = useCallback(async () => {
    if (!baseKey || !channelKey) return;
    setIsClearing(true);
    try {
      await clearChannelMessages({ baseKey, channelKey });
      setClearConfirmOpen(false);
    } catch (error) {
      console.error('[Home AI] clear failed', error);
    } finally {
      setIsClearing(false);
    }
  }, [channelKey, baseKey]);

  if (!selectedChannel) {
    return (
      <div className="ai-home-session__empty">
        This session is unavailable.
        <Button label="Back to Home" onClick={() => onBackToIdle?.()} />
      </div>
    );
  }

  return (
    <div
      className={[
        'channel-container',
        'ai-home-session',
        isThreadPanelOpen
          ? 'channel-container--thread-open'
          : (showSessionsPanel ? 'channel-container--sessions-open' : 'channel-container--no-side-pane'),
        showSessionsDrawer ? 'channel-container--sessions-drawer' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="channel-main-pane">
        <ChannelToolbar
          channel={selectedChannel}
          displayTitle={displayTitle}
          memberCount={1}
          canDeleteChannel={false}
          canClearConversation={canClearConversation}
          isClearingConversation={isClearing}
          clearConversationEnabled={!hasPendingAnalyze}
          showNewChatButton={!showSessionsPanel && !showSessionsDrawer}
          canManageChannel={false}
          showMembersButton={false}
          showSessionsButton
          sessionsPanelCollapsed={sessionsPanelCollapsed || isNarrow}
          hideIdentity={hideChannelIdentity}
          actionsPlacement={channelActionsBelow ? 'split' : 'header'}
          onSessionsClick={handleToggleSessions}
          onNewChatClick={() => onBackToIdle?.()}
          onClearClick={() => setClearConfirmOpen(true)}
        />

        <MessageFeed
          attachmentNodesByKey={attachmentNodesByKey}
          canReplyInThread
          currentUserKey={userKey}
          emptyPlaceholder="Ask about your knowledge base."
          errorMessage={messagesError || analyzeError || ''}
          hasUnreadRepliesByKey={hasUnreadRepliesByKey}
          membersByKey={membersByKey}
          mentionedPeopleByKey={mentionedPeopleByKey}
          messages={messages}
          loading={messagesLoading}
          hasMoreMessages={hasMoreMessages}
          isLoadingOlderMessages={isLoadingOlder}
          onLoadOlderMessages={loadOlderMessages}
          scrollToMessageKey={mainFeedScrollToMessageKey}
          showAiTyping={pendingCount > 0}
          aiPendingCount={pendingCount}
          showMessageEditDelete={false}
          threadSummariesByRootKey={threadSummariesByRootKey}
          threadRootsLimitedToFeedSurfaces
          onDeleteMessage={removeMessage}
          onEditMessage={updateMessage}
          onOpenAttachmentNode={handleOpenAttachmentNode}
          onOpenThread={handleOpenThread}
          onReplyInThread={handleOpenThread}
          onSelectDisambiguationOption={handleSelectDisambiguationOption}
        />

        {!(isThreadPanelOpen && isNarrow) ? (
          <ChannelPostForm
            disabled={!channelKey || !userKey}
            isSending={isSending}
            isAi
            mentionCandidates={mentionCandidates}
            channelMemberKeys={[userKey]}
            canInviteMembers={false}
            channelTitle={displayTitle}
            onSubmit={handlePostSubmit}
          />
        ) : null}

        {channelActionsBelow ? (
          <ShellSectionActions>
            <ChannelToolbar
              channel={selectedChannel}
              displayTitle={displayTitle}
              memberCount={1}
              canDeleteChannel={false}
              canClearConversation={canClearConversation}
              isClearingConversation={isClearing}
              clearConversationEnabled={!hasPendingAnalyze}
              showNewChatButton={!showSessionsPanel && !showSessionsDrawer}
              canManageChannel={false}
              showMembersButton={false}
              showSessionsButton={false}
              hideIdentity
              actionsPlacement="footer"
              onNewChatClick={() => onBackToIdle?.()}
              onClearClick={() => setClearConfirmOpen(true)}
            />
          </ShellSectionActions>
        ) : null}
      </div>

      {isThreadPanelOpen ? (
        <div className="channel-thread-pane">
          <ChannelThreadPanel
            rootMessage={activeThreadRootMessage}
            replies={threadReplies}
            loading={threadMessagesLoading}
            hasMoreReplies={hasMoreReplies}
            onLoadMoreReplies={loadMoreReplies}
            errorMessage={threadMessagesError}
            isSending={isThreadSending}
            disabled={!channelKey || !userKey}
            currentUserKey={userKey}
            attachmentNodesByKey={threadAttachmentNodesByKey}
            scrollToMessageKey={threadFeedScrollToMessageKey}
            mentionCandidates={mentionCandidates}
            channelMemberKeys={[userKey]}
            canInviteMembers={false}
            channelTitle={displayTitle}
            membersByKey={membersByKey}
            mentionedPeopleByKey={mentionedPeopleByKey}
            isAiThread
            showAiTyping={threadPendingAnalyzeCount > 0}
            aiPendingCount={threadPendingAnalyzeCount}
            useBackNavigation={isNarrow}
            onBack={handleCloseThread}
            onClose={handleCloseThread}
            onDeleteMessage={removeThreadMessage}
            onEditMessage={updateThreadMessage}
            onOpenAttachmentNode={handleOpenAttachmentNode}
            onSubmitReply={handleThreadPostSubmit}
          />
        </div>
      ) : showSessionsPanel ? (
        <div className="channel-sessions-pane">
          <AiSessionsPanel
            sessions={aiSessions}
            activeSessionKey={channelKey}
            baseKey={baseKey}
            user={user}
            onClose={handleCollapseSessions}
            onOpenSession={handleOpenSession}
            onSessionDeleted={handleSessionDeleted}
            onNewChat={onBackToIdle}
          />
        </div>
      ) : null}

      {showSessionsDrawer ? (
        <AiSessionsPanel
          sessions={aiSessions}
          activeSessionKey={channelKey}
          baseKey={baseKey}
          user={user}
          asDrawer
          onClose={handleCollapseSessions}
          onOpenSession={handleOpenSession}
          onSessionDeleted={handleSessionDeleted}
          onNewChat={onBackToIdle}
        />
      ) : null}

      <Confirm
        title="Clear Session"
        message="Clear all messages in this conversation?"
        confirmLabel="Clear"
        isOpen={clearConfirmOpen}
        setOpen={setClearConfirmOpen}
        onConfirm={handleClear}
      />

      <PeekPanel
        isOpen={isPeekOpen}
        node={peekLocalNode}
        isLoading={isPeekLoading}
        error={peekError}
        onClose={closePeek}
        onCoverSaved={handlePeekCoverSaved}
        onLocationSaved={handlePeekLocationSaved}
        onNodeChange={handlePeekNodeChange}
        onSubtitleSaved={handlePeekSubtitleSaved}
        onTagsSaved={handlePeekTagsSaved}
        onTitleSaved={handlePeekTitleSaved}
      />
    </div>
  );
});

AiHomeSession.displayName = 'AiHomeSession';

export default AiHomeSession;
